import { useState, useRef, useMemo } from 'react';
import { Formik, Form } from 'formik';
// Material UI
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import MuiTextField from '@mui/material/TextField';
// Local
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import {
    Box,
    IconButton, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { GridDeleteIcon } from '@mui/x-data-grid';
import { CifData } from 'BMapsSrc/util/cif';
import { PdbData } from 'BMapsSrc/util/pdb';
import { withTooltip, withFormik } from '../../../../common/helpers';
import TextField, { TextArea } from '../../../../common/TextFields';
import { SelectWithFormik } from '../../../../common/Select';
import { loadProteinFiles, loadSessionFiles } from '../../../../../paste_drag';
import { UserDataImportCase, PdbImportCase } from '../../../../../model/MapCase';
import { fetchPdbFile } from '../../../../../integrations/rcsb';
import { fileFilterAcceptedTypes } from '../../tab_utils';
import {
    getSupportedProteinTypes, guessFormat,
} from '../../../../../util/mol_format_utils';
import { WorkingIndicator } from '../../../../ui_utils';
import { DialogSubmitButton } from '../../../../common/DialogActionButtons';
import FormControlContainer from '../../base/FormControlContainer';
import FileDroppableTextAreaWithFormik from '../../base/TextAreaWithFormik';
import {
    InputBox, PlaceHolder, InputFile, UploadButton, FormControlLabelStyled, ImportErrors,
} from '../../import_components';

function PreserveHydrogensCheckbox({ ...rest }) {
    return (
        <FormControlLabelStyled
            control={(
                <Checkbox
                    color="primary"
                    {...rest}
                />
                )}
            label="Preserve Protein Hydrogens"
        />
    );
}

const PreserveHydrogensWithTooltip = withTooltip(PreserveHydrogensCheckbox);

export default function ImportProteinTab({
    selectProtein,
    closeImportPane,
    initData,
    errors,
}) {
    const uploadInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [ligandData, setLigandData] = useState([]);
    const [assemblyData, setAssemblyData] = useState([]);
    const [pdbDialogOpen, setPdbDialogOpen] = useState(false);
    const [pdbIdInput, setPdbIdInput] = useState('');
    const [pdbIdError, setPdbIdError] = useState('');
    // These initialFormValues are derived from the initData and saved in a useMemo
    // so they can be passed into Formik initialValues.
    // Formik has enableReinitialization so if the initData changes the form will refresh.
    const initialFormValues = useMemo(() => {
        // Defaults
        const ret = {
            proteinDataText: '',
            dataFormat: '',
            PDBid: '',
            proteinName: '',
            preserveHydrogens: false,
            nameMappings: '',
            biologicalAssembly: 'none',
        };
        if (!initData) {
            return ret;
        }
        // Different fields have different ways of being set by the init data
        const {
            proteinData='', dataFormat='', PDBid='', proteinName='', nameMappings='',
        } = initData;
        ret.proteinDataText = proteinData;
        ret.dataFormat = dataFormat || guessFormat(proteinData);
        ret.PDBid = PDBid;
        ret.proteinName = proteinName;
        ret.nameMappings = nameMappings;
        const info = infoForProteinLoadOptions(proteinData, ret.dataFormat);
        if (!info.error) {
            ret.preserveHydrogens = info.preserveHydrogens;
            setLigandData(info.ligands);
            setAssemblyData(info.assemblies);
        }
        // initData can override the guessed preserveH from infoForProteinLoadOptions
        if (initData.preserveHydrogens != null) ret.preserveHydrogens = initData.preserveHydrogens;
        return ret;
    }, [initData]);

    function validateProteinDataText(text) {
        return text === '' ? 'This field is required!' : undefined;
    }

    function validateDataFormat(format) {
        return format === '' ? 'This field is required!' : undefined;
    }

    function formValidate(formValues) {
        const formErrs = {};
        let val;

        val = validateProteinDataText(formValues.proteinDataText);
        if (val) {
            formErrs.proteinDataText = val;
        }

        val = validateDataFormat(formValues.dataFormat);
        if (val) {
            formErrs.dataFormat = val;
        }

        return formErrs;
    }

    function getMoltypeSelectComponents() {
        const moltypes = getSupportedProteinTypes();
        return (
            moltypes.map((moltype) => (
                <MenuItem
                    key={moltype}
                    value={moltype}
                >
                    {moltype.toUpperCase()}
                </MenuItem>
            ))
        );
    }

    function handleUploadBtnClicked() {
        if (!uploadInputRef.current) return;
        // Chrome doesn't fire onChange when uploading the same file!
        // reset the value so that change event fires.
        uploadInputRef.current.value = '';
        uploadInputRef.current.click();
    }

    function handleSubmit(formValues) {
        const {
            proteinName: name, PDBid: pdbid, dataFormat: format,
            proteinDataText: data, preserveHydrogens, nameMappings,
            biologicalAssembly,
        } = formValues;
        const ligandSmiles = ligandData.map((residue) => residue.smiles);
        const loadOptions = { preserveHydrogens, nameMappings, ligandSmiles };
        if (biologicalAssembly && biologicalAssembly !== 'none') loadOptions.biologicalAssembly = biologicalAssembly;
        const newMapCase = new UserDataImportCase({
            data, name, pdbid, format, loadOptions,
        });
        selectProtein(newMapCase, loadOptions);
        return false;
    }

    // Actually render the form
    return (
        <Formik
            initialValues={initialFormValues}
            enableReinitialize
            onSubmit={handleSubmit}
            validate={formValidate}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {/* Formik Render Function with parameters from the formik "bag" */}
            {({
                values, setFieldValue, getFieldProps,
            }) => {
                const { proteinDataText, preserveHydrogens } = values;

                function updateForm(proteinText) {
                    // this function changes form values based
                    // on the content of the protein text area
                    // it is called every time the text area changes,
                    // but form values don't necessarily need to be updated.
                    setFieldValue('proteinDataText', proteinText);

                    const format = guessFormat(proteinText);

                    // this checks if the form values need to be updated
                    const formatUpdate = true;
                    const hydrogensUpdate = true;
                    const smilesUpdate = true;

                    if (formatUpdate) {
                        setFieldValue('dataFormat', format);
                    }

                    if (!proteinText) {
                        clearParsedData();
                        return;
                    }

                    const loadOptionsInfo = infoForProteinLoadOptions(proteinText, format);
                    if (loadOptionsInfo.error) {
                        // parsing failed so set values to empty
                        clearParsedData();
                        return;
                    }

                    if (hydrogensUpdate) {
                        setFieldValue('preserveHydrogens', loadOptionsInfo.preserveHydrogens);
                    }

                    if (smilesUpdate) {
                        if (!ligandsMatch(loadOptionsInfo.ligands)) {
                            setLigandData(loadOptionsInfo.ligands);
                        }
                    }

                    setAssemblyData(loadOptionsInfo.assemblies);
                    setFieldValue('biologicalAssembly', 'none');
                }

                function ligandsMatch(newLigandData) {
                    if (ligandData.length === newLigandData.length
                        && ligandData.every(
                            (ligand, i) => ligand.resKey === newLigandData[i].resKey
                        )) {
                        return true;
                    }
                    return false;
                }

                function changeLigand(e, resKey) {
                    const newSmiles = ligandData.map((data) => {
                        if (data.resKey === resKey) {
                            return { ...data, smiles: e.target.value };
                        }
                        return data;
                    });
                    setLigandData(newSmiles);
                }

                function removeLigand(resKey) {
                    setLigandData(ligandData.map((data) => {
                        if (data.resKey === resKey) {
                            return { resKey, visible: false, smiles: '' };
                        }
                        return data;
                    }));
                }

                async function handleFileImport(file) {
                    setLoading(true);
                    if (await loadSessionFiles([file])) {
                        closeImportPane();
                        return;
                    }
                    const proteinSources = await loadProteinFiles([file]);
                    const proteinText = proteinSources[0]?.proteinData;

                    setLoading(false);

                    if (!proteinText) return;

                    updateForm(proteinText);
                }

                async function handlePdbDownload() {
                    const pdbId = pdbIdInput.trim();
                    if (!PdbImportCase.isValidId(pdbId)) {
                        setPdbIdError('Invalid PDB ID (e.g. "4RMG")');
                        return;
                    }
                    setPdbIdError('');
                    setPdbDialogOpen(false);
                    setLoading(true);
                    try {
                        const result = await fetchPdbFile(pdbId);
                        if (result.error) {
                            setPdbIdError(result.error);
                            setPdbDialogOpen(true);
                            return;
                        }
                        updateForm(result.text);
                        setFieldValue('PDBid', pdbId.toUpperCase());
                    } finally {
                        setLoading(false);
                    }
                }

                function clearParsedData() {
                    setLigandData([]);
                    setAssemblyData([]);
                    setFieldValue('preserveHydrogens', false);
                    setFieldValue('biologicalAssembly', 'none');
                }

                return (
                    <Form id="importForm" style={{ flex: '1 1 auto', overflowY: 'auto' }}>
                        {errors && <ImportErrors errors={errors} /> }
                        <PlaceHolder $show={proteinDataText === ''}>
                            { loading
                                ? <WorkingIndicator />
                                : (
                                    <>
                                        Drag & Drop or
                                        {' '}
                                        <UploadButton
                                            disableRipple
                                            onClick={handleUploadBtnClicked}
                                        >
                                            upload
                                        </UploadButton>
                                        {' '}
                                        a pdb, supported protein file, or BMaps session file,
                                        {' '}
                                        <br />
                                        or
                                        {' '}
                                        <UploadButton
                                            disableRipple
                                            onClick={() => setPdbDialogOpen(true)}
                                        >
                                            download
                                        </UploadButton>
                                        {' '}
                                        from the PDB,
                                        {' '}
                                        <br />
                                        or paste protein data and click &quot;Import Protein&quot;
                                    </>
                                )}
                        </PlaceHolder>
                        <FileDroppableTextAreaWithFormik
                            name="proteinDataText"
                            id="importMolText"
                            rows={1}
                            autoFocus
                            validate={validateProteinDataText}
                            onChange={(e) => updateForm(e.target.value)}
                            onDrop={(files) => handleFileImport(files[0])}
                            sx={{
                                '& textarea': {
                                    resize: 'vertical',
                                    minHeight: '7rem',
                                },
                                '&': {
                                    // Prevent textarea border from getting cut off at certain
                                    // resolutions/zoom levels, especially with selectivity control.
                                    margin: '3px',
                                },
                            }}
                        />
                        <InputBox>
                            <SelectWithFormik
                                name="dataFormat"
                                id="importMolFormat"
                                label="Data Format"
                                validate={validateDataFormat}
                                slotProps={{ select: { native: false } }}
                            >
                                <MenuItem value=""><em>Data Format</em></MenuItem>
                                {getMoltypeSelectComponents()}
                            </SelectWithFormik>
                            <TextField
                                fullWidth
                                name="PDBid"
                                id="importPDBid"
                                label="PDB ID (optional)"
                                variant="outlined"
                            />
                            <TextField
                                fullWidth
                                name="proteinName"
                                id="importProteinName"
                                label="Protein Name (optional)"
                                variant="outlined"
                            />
                            <FormControlContainer>
                                <InputFile
                                    inputRef={uploadInputRef}
                                    id="importFileSelect"
                                    type="file"
                                    onChange={(e) => handleFileImport(e.target.files[0])}
                                    slotProps={{
                                        htmlInput: {
                                            accept: fileFilterAcceptedTypes(
                                                getSupportedProteinTypes()
                                            ),
                                        },
                                    }}
                                />
                            </FormControlContainer>
                        </InputBox>
                        <Dialog open={pdbDialogOpen} onClose={() => setPdbDialogOpen(false)}>
                            <DialogTitle>Download from PDB</DialogTitle>
                            <DialogContent>
                                <MuiTextField
                                    autoFocus
                                    fullWidth
                                    label="PDB ID"
                                    placeholder="e.g. 4RMG"
                                    value={pdbIdInput}
                                    onChange={(e) => { setPdbIdInput(e.target.value); setPdbIdError(''); }}
                                    error={!!pdbIdError}
                                    helperText={pdbIdError}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handlePdbDownload();
                                        }
                                    }}
                                    variant="outlined"
                                    sx={{ mt: 1 }}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setPdbDialogOpen(false)}>Cancel</Button>
                                <Button variant="contained" onClick={handlePdbDownload}>Download</Button>
                            </DialogActions>
                        </Dialog>
                        <DialogContentSection
                            title="Advanced parameters"
                            collapsible
                            disablePaddingTop
                            disablePaddingBottom
                            collapsedMarginBottom="0.5em"
                        >
                            <PreserveHydrogensWithTooltip
                                {...getFieldProps('preserveHydrogens')}
                                checked={preserveHydrogens}
                                tooltip="Keep all protein hydrogen atoms as defined and do not add missing hydrogens.
                    All hydrogen atoms must have Amber compatible names.
                    Recommended for structures that have been prepared with other software, or for NMR structures."
                            />
                            {assemblyData.length > 0 && (
                                <SelectWithFormik
                                    name="biologicalAssembly"
                                    id="importBiologicalAssembly"
                                    label="Biological Assembly"
                                    SelectProps={{ native: false }}
                                >
                                    <MenuItem value="none"><em>None (asymmetric unit)</em></MenuItem>
                                    {assemblyData.map((a) => (
                                        <MenuItem key={a.id} value={a.id}>
                                            {`${a.id} — ${a.oligomericDetails || a.details || ''}`}
                                        </MenuItem>
                                    ))}
                                </SelectWithFormik>
                            )}
                            {ligandData.filter((d) => d.visible).length > 0 && (
                                <Box sx={{ margin: '1rem 0' }}>
                                    <ContainerCellWithTooltip tooltip="Provide SMILES strings for determining ligand bond orders and hydrogens.">
                                        Ligand&nbsp;Definitions
                                    </ContainerCellWithTooltip>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>
                                                    SMILES
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {ligandData
                                                .filter((data) => data.visible)
                                                .map((data) => (
                                                    <TableRow key={data.resKey}>
                                                        <TableCell>
                                                            {data.resKey}
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextArea
                                                                size="small"
                                                                value={data.smiles}
                                                                onChange={(e) => {
                                                                    changeLigand(
                                                                        e, data.resKey
                                                                    );
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton>
                                                                <GridDeleteIcon
                                                                    onClick={
                                                                () => removeLigand(data.resKey)
                                                            }
                                                                />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                            <TextAreaWithTooltip
                                id="importNameMappings"
                                label="Name Mappings (optional)"
                                fullWidth
                                minRows={1}
                                variant="outlined"
                                style={{ flexGrow: 1, height: 'unset', marginBottom: '0.5ex' }}
                                {...getFieldProps('nameMappings')}
                                tooltip='Specify name mappings for atoms, residues, or chains.
                        See the "Viewing Proteins" section of the Guide for more information'
                            />
                        </DialogContentSection>
                        <DialogSubmitButton style={{ width: '100%' }}>Import Protein</DialogSubmitButton>
                    </Form>
                );
            }}
        </Formik>
    );
}

const TextAreaWithFormik = withFormik(TextArea, { hasErrorState: false });
const TextAreaWithTooltip = withTooltip(TextAreaWithFormik, {
    ContainerProps: {
        style: { paddingTop: '0.5ex' },
    },
});

const ContainerCellWithTooltip = withTooltip(Box, {
    IconWrapperProps: {
        style: { padding: 0 },
    },
});

/**
 * Given protein data, determine values for some of the UI controls:
 * 1. Whether to check "preserve protein hydrogens"
 * 2. Ligand entries for collecting ligand smiles
 * 3. Biological assemblies available in the CIF data
 * @param {string} proteinText protein data (pdb/cif)
 * @param {string} format data format: pdb|cif
 * @returns {{
 *     preserveHydrogens: boolean,
 *     ligands: Array<{ resKey: string, smiles: string, visible: boolean }>,
 *     assemblies: Array<{ id: string, details: string,
 *         oligomericDetails: string, oligomericCount: string }>,
 *     error: string,
 * }}
 */
function infoForProteinLoadOptions(proteinText, format) {
    const ret = { preserveHydrogens: false, ligands: [], assemblies: [] };
    if (!proteinText || !format) {
        return ret;
    }
    try {
        let data;
        if (format === 'cif') {
            data = new CifData(proteinText);
        } else if (format === 'pdb') {
            data = new PdbData(proteinText);
        } else {
            return { error: `Unknown format: ${format}` };
        }

        const residuesWithH = data.filterResidues({ hetflag: false, hasH: true });
        ret.preserveHydrogens = residuesWithH.length > 0;

        ret.ligands = data.filterResidues({ hetflag: true, isWater: false })
            .map((lig) => ({ resKey: lig.resKey, smiles: '', visible: !lig.isSingle && !lig.hasH }));

        ret.assemblies = (format === 'cif' && data.assemblies) ? data.assemblies : [];
        return ret;
    } catch (ex) {
        console.warn(`Failed to parse ${format} data, so can't take advantage of parsed fields. Error: ${ex}`);
        return { error: ex };
    }
}
